/**
 * Shrink a user-picked image to a max dimension before base64-encoding
 * for upload. Two goals:
 *   1. Cut upload bandwidth — a modern phone camera dumps 3-5MB HEIC/JPEG
 *      per frame, and after base64 (~1.33×) that easily blows past the
 *      body-parser cap and burns cellular data.
 *   2. Skip the round trip on obviously-oversized inputs — the BE resizes
 *      to 1024px anyway, so shipping 4032×3024 pixels is pure waste.
 *
 * Aspect-preserving `contain` — we don't crop client-side because the BE's
 * sharp pipeline does the final square crop and any FE crop would either
 * duplicate work or clash with the BE's centre-pos choice. All output as
 * JPEG at 0.9 — quality survives the second (BE) re-encode fine.
 *
 * Returns a data URL ready to POST as `imageDataUrl`.
 */
export async function resizeImageForUpload(file: File, maxDim = 2048): Promise<string> {
  const bitmap = await loadBitmap(file);
  const { width, height } = bitmap;
  const scale = Math.min(1, maxDim / Math.max(width, height));
  const outW = Math.round(width * scale);
  const outH = Math.round(height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(bitmap, 0, 0, outW, outH);
  // Release the decoded bitmap ASAP — a 4032×3024 ImageBitmap sits at
  // ~48MB in memory until GC. Not free on a low-end phone.
  if ('close' in bitmap && typeof (bitmap as any).close === 'function') {
    (bitmap as any).close();
  }
  return canvas.toDataURL('image/jpeg', 0.9);
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  // createImageBitmap is faster + off-thread on modern browsers. Fall
  // back to <img> for older iOS Safari where it's flaky with HEIC.
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // fall through
    }
  }
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}
